import classnames from 'classnames/bind';
import { forwardRef, useCallback, useMemo } from 'react';
import { Header, THeaderVariant } from '@portals/external-services-header';
import {
  ButtonSecondary,
  ButtonInterface,
  SelectOptionType,
} from '@portals/ui-kit';
import { useTranslations } from 'next-intl';
import { TColorType } from '@portals/ui-kit/lib/chips/types';
import { currentCountryByHost, currentZone } from '@portal-root/geo-utils';
import { TSelectOption } from '@portals/external-services-header/lib/_components/header/_types';
import { ELoginButtonPagePosition, EAnalytics } from '@/_constants/analytics';
import { navigateHandle } from '@/_utils/navigate-handle';
import { useAnalytics } from '@/_hooks/use-analytics';
import { EColors, ECountries } from '@/_types';
import { redirect } from '@/_utils/redirect';
import { useWindowSize } from '@/_hooks/use-window-size';
import { isDesktop } from '@/_utils/is-desktop';
import { useLocaleCountry } from '@/_hooks/use-locale-country';
import { getActionButtons } from '../_utils/get-action-buttons';
import { EHeaderModifier, THeaderConfig } from '../_types';
import { Logo } from '../_components/logo';
import { AZ_COUNTRY_CODE, BREACKPOINT_DESKTOP_MIN } from '../_constants';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'HeaderMobile';

type TProps = {
  modifier: EHeaderModifier;
  handleChangeCountry: (option: SelectOptionType) => void;
  handleChangeLanguage: (option: SelectOptionType) => void;
};

export const HeaderMobile = forwardRef<HTMLDivElement, TProps & THeaderConfig>(
  function HeaderMobileFunction(
    {
      modifier,
      handleChangeCountry,
      handleChangeLanguage,
      languages,
      countries,
      headerLinks,
      headerPopupLinks,
      loginButton,
      registerButton,
    },
    ref,
  ) {
    const translations = useTranslations();
    const sendEvent = useAnalytics();
    const { country, locale } = useLocaleCountry();
    const { width } = useWindowSize();
    const isDesktopWidth = isDesktop({
      width,
      breakpointMin: BREACKPOINT_DESKTOP_MIN,
    });

    const isTransparent = modifier === EHeaderModifier.TRANSPARENT;
    const isAzerbaijan = currentCountryByHost === AZ_COUNTRY_CODE;

    const loginButtonColor: TColorType | undefined = isTransparent
      ? loginButton.color
      : 'primary';
    const registerButtonColor: TColorType | undefined = isTransparent
      ? registerButton.color
      : 'secondary';

    const isTurkey = currentZone.endsWith(ECountries.TR);
    const variant: THeaderVariant =
      isTransparent && !isTurkey ? 'transparent' : 'default';
    const logoColor = isTransparent ? EColors.WHITE : EColors.BLACK;

    const headerMenuOptions = useMemo(() => {
      return [...headerLinks, ...headerPopupLinks].map(
        ({ id, title, routeName, isMenuLink }) => ({
          id,
          title: translations(title),
          routeName,
          isMenuLink,
        }),
      );
    }, [headerLinks, headerPopupLinks, translations]);

    const selectedLanguage = useMemo(
      () => languages?.find(({ id }) => locale === id),
      [languages, locale],
    );

    const selectedCountry = useMemo(
      () => countries.find(({ id }) => country === id),
      [countries, country],
    );

    const formattedCountries = useMemo(() => {
      return countries.map((item) => {
        const color = isTransparent ? EColors.WHITE : EColors.BLACK;
        const DropdownIcon =
          typeof item.icon === 'string' ? (
            item.icon
          ) : (
            <item.icon color={EColors.BLACK} />
          );
        const FlagIcon =
          typeof item.icon === 'string' ? (
            item.icon
          ) : (
            <item.icon color={color} />
          );

        return { ...item, icon: FlagIcon, dropdownIcon: DropdownIcon };
      });
    }, [countries, isTransparent]);

    const handleRedirect = useCallback(
      (position: ELoginButtonPagePosition) => {
        sendEvent({
          name: EAnalytics.SUPPLIER_LANDING_AUTH_BUTTON_T,
          // eslint-disable-next-line camelcase
          params: { position_button: position },
        });
        redirect();
      },
      [sendEvent],
    );

    const handleLogin = useCallback(
      () => handleRedirect(ELoginButtonPagePosition.LOGIN),
      [handleRedirect],
    );

    const handleRegister = useCallback(
      () => handleRedirect(ELoginButtonPagePosition.UP_NEAR_LOGIN),
      [handleRedirect],
    );

    const handleMenuItemClick = (item: TSelectOption): void => {
      if (item.isMenuLink) {
        sendEvent({
          name: EAnalytics.SUPPLIER_LANDING_MENU_HEADER_T,
          params: {
            language: locale,
            // eslint-disable-next-line camelcase
            button_name: item.id,
          },
        });
      }

      if (item.routeName?.startsWith('#')) {
        return navigateHandle({ routeName: item.routeName });
      }

      window.open(item.routeName, '_blank');
    };

    const handleOpenMenu = (isMenuOpen: boolean): void => {
      if (isMenuOpen) {
        sendEvent({
          name: EAnalytics.SUPPLIER_LANDING_MENU_V,
          params: {
            language: locale,
          },
        });
      }

      if (headerLinks.length) {
        sendEvent({
          name: EAnalytics.SUPPLIER_LANDING_MENU_HEADER_V,
          params: {
            language: locale,
          },
        });
      }
    };

    return (
      <div ref={ref} className={cn(BLOCK_NAME)}>
        <Header
          actionButtons={{
            firstButton: {
              text: translations(loginButton.text),
              onClick: handleLogin,
              ...getActionButtons({
                isTransparent,
                color: loginButtonColor,
                colorHex: loginButton.colorHex,
                textHex: loginButton.textHex,
              }),
            },
            secondButton: {
              text: translations(registerButton.text),
              ...getActionButtons({
                isTransparent,
                color: registerButtonColor,
                colorHex: registerButton.colorHex,
                textHex: registerButton.textHex,
              }),
              onClick: handleRegister,
            },
          }}
          countrySelectParams={
            formattedCountries.length
              ? {
                  options: formattedCountries ?? [],
                  value: selectedCountry?.id ?? '',
                  onChange: handleChangeCountry,
                }
              : undefined
          }
          createInstance
          customLogo={isAzerbaijan ? undefined : <Logo logoColor={logoColor} />}
          isMobileView={!isDesktopWidth}
          languageSelectParams={
            !!languages?.length
              ? {
                  options: languages,
                  onChange: handleChangeLanguage,
                  value: selectedLanguage?.id,
                }
              : undefined
          }
          menuParams={{
            mobile: {
              options: headerMenuOptions,
              onClick: handleMenuItemClick,
              onOpenChange: handleOpenMenu,
              tertiaryLine: {
                sbActions: {
                  Layout: 'Vertical',
                  isButtonFullWidth: true,
                  ActionWidth: 'Full',
                  firstCustomItem: (
                    <ButtonSecondary
                      fullWidth
                      onClick={handleLogin}
                      text={translations(loginButton.text)}
                    />
                  ),
                  secondCustomItem: (
                    <ButtonInterface
                      fullWidth
                      onClick={handleRegister}
                      text={translations(registerButton.text)}
                    />
                  ),
                },
              },
            },
          }}
          variant={variant}
        />
      </div>
    );
  },
);
